<template>
  <div class="rd-screen">
    <report-tools :copy="slips.length > 0" @copy="copyOrders">
      <div v-if="shifts.length" class="rd-chips" role="group" :aria-label="$t('shifts')">
        <button
          v-for="shift in shifts"
          :key="shift"
          type="button"
          class="rd-chip"
          :aria-pressed="String(selectedShift === shift)"
          @click="selectedShift = shift"
        >
          <report-shift-tag :shift="Number(shift)" long></report-shift-tag>
        </button>
      </div>
    </report-tools>

    <report-state v-if="dayLoading" kind="loading"></report-state>
    <report-state
      v-else-if="!slips.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <section v-else class="rd-slips">
      <div class="rd-slips__head">
        <report-shift-tag :shift="Number(selectedShift)" long></report-shift-tag>
        <i class="rd-order-card__rule"></i>
        <span class="rd-num">{{ summary }}</span>
      </div>

      <div class="rd-slips__grid">
        <article v-for="slip in slips" :key="slip.key" class="rd-slip" :data-shift="selectedShift">
          <header class="rd-slip__head">
            <span class="rd-avatar rd-avatar--sm">{{ slip.initials }}</span>
            <b class="rd-slip__name" :title="`ID ${slip.userId}`">{{ slip.name }}</b>
            <span v-if="isRange" class="rd-slip__no rd-num">{{ slip.dateText }}</span>
            <span v-else class="rd-slip__no rd-num">#{{ slip.userId }}</span>
          </header>
          <ul>
            <template v-for="category in slip.categories">
              <li v-if="category.name" :key="`${category.id}-title`" class="gm-slip__cat">{{ category.name }}</li>
              <li v-for="(meal, index) in category.meals" :key="`${category.id}-${index}`">
                <b class="rd-num">{{ meal.quantity }}× </b>
                <span>{{ meal.name }}</span>
              </li>
            </template>
          </ul>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import CompanyReportTabOrders from "@/components/company/reports/tabs/CompanyReportTabOrders.vue";
import hasPlural from "@/components/report/shared/hasPlural";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasCompanyGroupOrderSlips from "../../hasCompanyGroupOrderSlips";

/**
 * Naručeno firme tipa "grupa" na računaru: jedna smena (oznake smena u alatima), kartica po radniku sa obrocima po
 * grupama jela (za period i po danu). Firme, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje,
 * štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopGroupOrdered",
  extends: CompanyReportTabOrders,
  mixins: [hasPlural, hasCompanyGroupOrderSlips],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    summary() {
      return [
        this.pluralT("report_mobile.workers_n", this.workerTotal),
        this.pluralT("report_mobile.meals_n", this.mealTotal),
      ].join(" · ");
    },
    emptyTitle() {
      return this.searchText
        ? this.$t("report_mobile.no_results_for", { search: this.$store.state.report.filters.search })
        : this.$t("no_orders");
    },
    emptyText() {
      return this.searchText ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.workerTotal, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: this.mealTotal },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.slips.length ? kpis : []);
      },
      immediate: true,
    },
    dayLoading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyOrders() {
      const headers = [this.$t("worker"), ...(this.isRange ? [this.$t("date")] : []), this.$t("report_group.categories"), this.$t("meals")];
      const rows = this.slips.flatMap((slip) =>
        slip.categories.map((category) => [
          slip.name,
          ...(this.isRange ? [slip.dateText] : []),
          category.name,
          category.meals.map((meal) => `${meal.quantity}× ${meal.name}`).join("; "),
        ])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
